import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

import { Activity } from '../types';
import BreathingActivity from '../components/activities/BreathingActivity';
import WritingActivity from '../components/activities/WritingActivity';
import MeditationActivity from '../components/activities/MeditationActivity';
import PianoActivity from '../components/activities/PianoActivity';

const ActivityPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const [showCompletion, setShowCompletion] = useState(false);
  const [activityDuration, setActivityDuration] = useState(0);

  // URL 파라미터에서 선택된 액티비티와 감정 가져오기
  const selectedActivityId = searchParams.get('selected');
  const emotion = searchParams.get('emotion') || '평온';

  // 다국어 지원 액티비티 목록 생성
  const getActivities = () => [
    {
      id: 'meditation',
      name: t('activity.meditation.name'),
      description: t('activity.meditation.description'),
      icon: '🧘',
      color: 'bg-blue-100 text-blue-600'
    },
    {
      id: 'breathing',
      name: t('activity.breathing.name'),
      description: t('activity.breathing.description'),
      icon: '🌬️',
      color: 'bg-green-100 text-green-600'
    },
    {
      id: 'piano',
      name: t('activity.piano.name'),
      description: t('activity.piano.description'),
      icon: '🎹',
      color: 'bg-purple-100 text-purple-600'
    },
    {
      id: 'sketch',
      name: t('activity.sketch.name'),
      description: t('activity.sketch.description'),
      icon: '🎨',
      color: 'bg-orange-100 text-orange-600'
    },
    {
      id: 'voice',
      name: t('activity.voice.name'),
      description: t('activity.voice.description'),
      icon: '🎤',
      color: 'bg-pink-100 text-pink-600'
    },
    {
      id: 'writing',
      name: t('activity.writing.name'),
      description: t('activity.writing.description'),
      icon: '✍️',
      color: 'bg-yellow-100 text-yellow-600'
    }
  ];

  useEffect(() => {
    if (selectedActivityId) {
      const activities = getActivities();
      const activity = activities.find(a => a.id === selectedActivityId);
      if (activity) {
        setSelectedActivity(activity);
      }
    }
  }, [selectedActivityId, t]);

  const handleActivityComplete = () => {
    setShowCompletion(true);
  };

  const handleContinueToSoul = () => {
    navigate('/chat');
  };

  const handleBackToActivities = () => {
    setSelectedActivity(null);
    setShowCompletion(false);
    setActivityDuration(0);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // 액티비티별 컴포넌트 렌더링
  const renderActivityComponent = () => {
    if (!selectedActivity) return null;

    const commonProps = {
      emotion,
      onComplete: handleActivityComplete
    };

    switch (selectedActivity.id) {
      case 'breathing':
        return <BreathingActivity {...commonProps} />;
      case 'writing':
        return <WritingActivity {...commonProps} />;
      case 'meditation':
        return <MeditationActivity {...commonProps} />;
      case 'piano':
        return <PianoActivity onComplete={handleActivityComplete} />;
      case 'sketch':
        return (
          <div className="text-center">
                      <div className="text-5xl mb-5">🎨</div>
          <h2 className="text-xl font-bold text-gray-900 mb-3">
            {t('activity.sketch.title')}
          </h2>
          <p className="text-sm text-gray-600 mb-5">
            {t('activity.sketch.title.coming')}
          </p>
          <button
            onClick={handleActivityComplete}
            className="btn-primary px-6 py-2.5"
          >
            {t('activity.complete')}
          </button>
          </div>
        );
      case 'voice':
        return (
          <div className="text-center">
                      <div className="text-5xl mb-5">🎤</div>
          <h2 className="text-xl font-bold text-gray-900 mb-3">
            {t('activity.voice.title')}
          </h2>
          <p className="text-sm text-gray-600 mb-5">
            {t('activity.voice.title.coming')}
          </p>
          <button
            onClick={handleActivityComplete}
            className="btn-primary px-6 py-2.5"
          >
            {t('activity.complete')}
          </button>
          </div>
        );
      default:
        return null;
    }
  };

  if (showCompletion) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="card p-6 text-center">
          <div className="text-5xl mb-5">🎉</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            {selectedActivity?.name} {t('activity.completion.title')}
          </h1>
          <p className="text-sm text-gray-600 mb-5">
            {emotion}{t('activity.completion.emotion.resolved').replace('{activity}', selectedActivity?.name || '')}
          </p>
          <div className="bg-blue-50 rounded-2xl p-5 mb-5">
            <h3 className="text-base font-semibold mb-3">{t('activity.completion.summary')}</h3>
            <div className="grid grid-cols-2 gap-3 text-center">
              <div>
                <div className="text-xl font-bold text-mint">{selectedActivity?.name}</div>
                <div className="text-xs text-gray-600">{t('routine.activity')}</div>
              </div>
              <div>
                <div className="text-xl font-bold text-mint">{emotion}</div>
                <div className="text-xs text-gray-600">{t('routine.emotion')}</div>
              </div>
            </div>
          </div>
          <div className="flex justify-center space-x-3">
            <button
              onClick={handleContinueToSoul}
              className="btn-primary px-6 py-2.5"
            >
              {t('activity.completion.continue')}
            </button>
            <button
              onClick={handleBackToActivities}
              className="btn-secondary px-6 py-2.5"
            >
              {t('activity.completion.another')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (selectedActivity) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="card p-5 mb-5">
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            {selectedActivity.name}
          </h1>
          <p className="text-sm text-gray-600">
            {selectedActivity.description}
          </p>
        </div>

        <div className="card p-6">
          {renderActivityComponent()}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="card p-5 mb-5">
        <h1 className="text-xl font-bold text-gray-900 mb-2">
          {t('activity.title')}
        </h1>
        <p className="text-sm text-gray-600">
          {t('activity.subtitle')}
        </p>
      </div>

      <div className="card p-5 mb-5">
        <h2 className="text-lg font-semibold mb-3">{t('activity.select')}</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {getActivities().map((activity) => (
            <button
              key={activity.id}
              onClick={() => setSelectedActivity(activity)}
              className="card p-3 text-left hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center space-x-2.5">
                <span className="text-xl">{activity.icon}</span>
                <div>
                  <h3 className="font-semibold text-sm text-gray-900">
                    {activity.name}
                  </h3>
                  <p className="text-xs text-gray-600">
                    {activity.description}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ActivityPage; 